import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
import { supabase } from '@/lib/supabaseClient';
import { canUserViewScreen } from '@/services/permissionService';
import toastStore from '@/utils/toastService';
import { useI18n } from 'vue-i18n';

/**
 * Permission guard for Vue Router
 * This guard checks if the user has permission to view the route based on the screen key
 * Screen keys can be provided either in the route's meta.screenKey or derived from the route path
 */
export const permissionGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  // Check if bypass_auth parameter is present in the URL
  if (to.query.bypass_auth === 'true') {
    return next();
  }
  
  // Check if app is locked
  const isAppLocked = sessionStorage.getItem('app_locked') === 'true';
  
  // If app is locked and not trying to access the lock screen, redirect to lock screen
  if (isAppLocked && to.path !== '/authentication/lock-screen' && 
      !to.path.includes('/authentication/sign-in') && 
      !to.path.includes('/authentication/logout')) {
    return next('/authentication/lock-screen');
  }
  
  // Skip permission check for authentication routes, root path, and error pages
  // Also skip for public frontend pages
  if (
    to.path === '/' || 
    to.path === '/features' ||
    to.path === '/team' ||
    to.path === '/faq' ||
    to.path === '/contact' ||
    to.path === '/documentation' ||
    to.path.startsWith('/api-overview') ||
    to.path.startsWith('/endpoint') ||
    to.path === '/dashboard' ||
    to.path === '/notifications' ||
    to.path === '/my-profile' ||
    to.path === '/extra-pages/search' ||
    to.path.startsWith('/auth') || 
    to.path.startsWith('/error') ||
    to.path === '/404' ||
    to.path === '/403' ||
    to.path === '/500'
  ) {
    return next();
  }

  // Check if user is authenticated
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session) {
    // Redirect to login if not authenticated
    return next({ path: '/auth/login', query: { redirect: to.fullPath } });
  }

  const userId = session.user.id;
  
  // Get screen key from route meta or path
  let screenKey = to.meta.screenKey as string;
  
  // If screenKey is not provided in meta, derive it from the path
  if (!screenKey) {
    // Remove leading slash and get the path parts
    const pathParts = to.path.substring(1).split('/');
    
    // Special handling for project management screens
    if (pathParts[0] === 'project-management') {
      if (pathParts.length === 1) {
        // Main project management screen
        screenKey = 'project_management';
      } else if (pathParts.length >= 2) {
        // Sub-screen of project management
        switch (pathParts[1]) {
          case 'project-overview':
            screenKey = 'project_overview';
            break;
          case 'projects-list':
            screenKey = 'projects_list';
            break;
          case 'create-project':
            screenKey = 'create_project';
            break;
          case 'clients':
            screenKey = 'project_management_clients';
            break;
          case 'teams':
            screenKey = 'project_management_teams';
            break;
          case 'kanban-board':
            screenKey = 'project_management_kanban_board';
            break;
          case 'users':
            screenKey = 'project_management_users';
            break;
          default:
            screenKey = 'project_management';
        }
      }
    } 
    // Special handling for real estate screens
    else if (pathParts[0] === 'real-estate') {
      if (pathParts.length === 1) {
        // Main real estate screen
        screenKey = 'real_estate';
      } else if (pathParts.length >= 2) {
        // Sub-screen of real estate
        switch (pathParts[1]) {
          case 'property-list':
            screenKey = 'property_list';
            break;
          case 'property-details':
            screenKey = 'property_details';
            break;
          case 'add-property':
            screenKey = 'add_property';
            break;
          case 'agents':
            screenKey = 'agents';
            break;
          case 'agent-details':
            screenKey = 'agent_details';
            break;
          case 'add-agent':
            screenKey = 'add_agent';
            break;
          case 'customers':
            screenKey = 'real_estate_customers';
            break;
          default:
            screenKey = 'real_estate';
        }
      }
    } 
    else {
      // Default behavior for other screens - use first part of path
      screenKey = pathParts[0];
    }
  }
  
  console.log(`Checking permission for screen key: ${screenKey} for path: ${to.path}`);
  
  // Skip permission check if no screen key available
  if (!screenKey) {
    console.warn('No screen key found for route', to.path);
    return next();
  }

  // Check if user has permission to view this screen
  const hasPermission = await canUserViewScreen(userId, screenKey);
  
  if (hasPermission) {
    return next();
  } else {
    console.warn(`User ${userId} does not have permission to view screen ${screenKey}`);
    
    // Get the current locale from localStorage or default to 'he'
    const currentLocale = localStorage.getItem('language') || 'he';
    
    // Define translations
    const translations = {
      he: 'אין לך הרשאה לצפות בדף זה. אנא פנה למנהל המערכת.',
      en: 'You do not have permission to view this page. Please contact your administrator.',
      ar: 'ليس لديك صلاحية لعرض هذه الصفحة. يرجى الاتصال بمدير النظام.'
    };
    
    const errorMessage = translations[currentLocale as keyof typeof translations] || translations.he;
    
    // Instead of redirecting to 403, show a toast and stay on the current page
    if (from.path !== '/') {
      // Show toast error message
      toastStore.show(
        errorMessage,
        'error',
        8000
      );
      
      // Stay on the current page
      return next(false);
    } else {
      // If we don't have a valid from route, redirect to dashboard or home
      toastStore.show(
        errorMessage,
        'error',
        8000
      );
      return next({ path: '/' });
    }
  }
};

export default permissionGuard; 